import { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import toast from "react-hot-toast";
import { fr } from "yup-locales";
import { setLocale } from "yup";
import * as yup from "yup";
import { User, UserFormData } from "@/redux/services/user/user.type";
import {
  useConfirmIdentifierChangeMutation,
  useCreateOrUpdateUserMutation,
  useRequestIdentifierChangeMutation,
} from "@/redux/services/user/user.api";
import { cleanerError } from "@/lib/utils";
import { useAppDispatch, useAppSelector } from "@/redux/features/hooks";
import { setCurrentUser } from "@/redux/features/user/user.slice";
import { useUploadMedia } from "@/lib/useUploadMedia";
import {
  identifierChangeErrorMessage,
  isPendingChangeGone,
} from "@/lib/identifierChange";

setLocale(fr);

// Phone and email are login identifiers (SFAPI-207): the name is saved
// directly, but a new phone or email only takes effect after the user enters
// the code the API sends to it.
const profileValidationSchema = yup.object().shape({
  fullName: yup.string().required().label("Nom"),
  phone: yup.string().trim().required().label("Téléphone"),
  email: yup.string().trim().email().label("Email"),
});

type ProfileFormData = yup.InferType<typeof profileValidationSchema>;

type IdentifierChange = { phone: string } | { email: string };

const describeIdentifier = (identifier: string) =>
  identifier.includes("@") ? `à ${identifier}` : `au ${identifier}`;

export const useEditProfil = () => {
  const [createOrUpdate, { isLoading: isSaving }] =
    useCreateOrUpdateUserMutation();
  const [requestChange, { isLoading: isRequesting }] =
    useRequestIdentifierChangeMutation();
  const [confirmChange, { isLoading: isConfirming }] =
    useConfirmIdentifierChangeMutation();
  const current = useAppSelector((s) => s?.user?.currentUser);
  const dispatch = useAppDispatch();

  const [pendingIdentifier, setPendingIdentifier] = useState<string | null>(
    current?.pendingIdentifier ?? null
  );
  const [isVerifying, setIsVerifying] = useState(false);
  const [codeError, setCodeError] = useState<string | null>(null);

  const {
    register,
    handleSubmit,
    formState: { errors },
    clearErrors,
  } = useForm<ProfileFormData>({
    resolver: yupResolver(profileValidationSchema),
  });

  useEffect(() => {
    if (errors) {
      cleanerError(errors, clearErrors);
    }
  }, [errors, clearErrors]);

  const sendCode = async (change: IdentifierChange): Promise<boolean> => {
    const res = await requestChange(change);
    if ("error" in res) {
      toast.error(identifierChangeErrorMessage(res.error));
      return false;
    }
    const identifier = "phone" in change ? change.phone : change.email;
    setPendingIdentifier(identifier);
    setCodeError(null);
    setIsVerifying(true);
    toast.success(
      `Un code de vérification a été envoyé ${describeIdentifier(identifier)}.`
    );
    return true;
  };

  const onSubmit = async ({ fullName, phone, email }: ProfileFormData) => {
    const newPhone = phone.trim();
    const newEmail = (email ?? "").trim();
    const phoneChanged = newPhone !== (current?.phone ?? "");
    const emailChanged = newEmail !== (current?.email ?? "");

    if (phoneChanged && emailChanged) {
      toast.error("Modifiez le téléphone ou l'email, un seul à la fois.");
      return;
    }
    if (emailChanged && newEmail === "") {
      toast.error("Pour retirer votre email, contactez le support ScaniFood.");
      return;
    }

    if (!phoneChanged && !emailChanged) {
      const res = await createOrUpdate({
        id: current?.id,
        data: { fullName } as UserFormData,
      });
      if ("data" in res) {
        // PATCH returns user:read only: keep what /users/current added.
        dispatch(setCurrentUser({ ...current, ...(res.data as User) } as User));
        toast.success(`Informations mis à jour avec succès`);
      } else if ("error" in res) {
        toast.error(identifierChangeErrorMessage(res.error));
      }
      return;
    }

    if (fullName !== (current?.fullName ?? "")) {
      const res = await createOrUpdate({
        id: current?.id,
        data: { fullName } as UserFormData,
      });
      if ("data" in res) {
        dispatch(setCurrentUser({ ...current, ...(res.data as User) } as User));
      }
    }

    await sendCode(phoneChanged ? { phone: newPhone } : { email: newEmail });
  };

  const confirmCode = async (code: string) => {
    const res = await confirmChange({ code: code.trim() });
    if ("data" in res) {
      const updated = res.data as User;
      dispatch(setCurrentUser({ ...current, ...updated } as User));
      const wasEmail = pendingIdentifier?.includes("@") ?? false;
      setPendingIdentifier(null);
      setIsVerifying(false);
      setCodeError(null);
      toast.success(
        wasEmail
          ? "Votre email a été mis à jour."
          : "Votre numéro de téléphone a été mis à jour."
      );
      return;
    }
    const { error } = res as { error: unknown };
    if (isPendingChangeGone(error)) {
      setPendingIdentifier(null);
      setIsVerifying(false);
      toast.error(identifierChangeErrorMessage(error));
      return;
    }
    setCodeError(identifierChangeErrorMessage(error));
  };

  const resendCode = async () => {
    if (!pendingIdentifier) return;
    await sendCode(
      pendingIdentifier.includes("@")
        ? { email: pendingIdentifier }
        : { phone: pendingIdentifier }
    );
  };

  return {
    register,
    errors,
    onSubmit: handleSubmit(onSubmit),
    isLoading: isSaving || isRequesting,
    pendingIdentifier,
    isVerifying,
    codeError,
    isConfirming,
    isRequesting,
    openVerification: () => setIsVerifying(true),
    closeVerification: () => {
      setIsVerifying(false);
      setCodeError(null);
    },
    confirmCode,
    resendCode,
  };
};

export const useUpdateProfilImage = () => {
  const { currentUser } = useAppSelector((s) => s?.user);
  const [avatar, setAvatar] = useState<string>();
  const [update] = useCreateOrUpdateUserMutation();
  const { uploadMedia } = useUploadMedia();
  const dispatch = useAppDispatch();

  const handleUpdateImage = async (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files) {
      const file = e.target.files[0];
      setAvatar(URL.createObjectURL(file));
      const media = await uploadMedia(file);
      if (media && "id" in media) {
        const data = {
          newImageUrl: media?.url,
          image: `/api/media_objects/${media?.id}`,
        };

        const res = await update({
          id: currentUser?.id,
          data: data as UserFormData,
        });

        if ("data" in res) {
          dispatch(setCurrentUser(res.data as User));
          toast.success("Photo de profil mis à jour avec succès !");
        }
      }
    }
  };

  return {
    handleUpdateImage,
    avatar,
    currentUser,
  };
};
